import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useStores } from '../hooks/useDB';
import PageLoader from '../components/PageLoader';
import {
  Gamepad2, Shuffle, Swords, Cat, Crown, Users, Smile, Lightbulb, Trophy, Info, Mic2,
} from 'lucide-react';
import { toast } from '../utils/toast';
import {
  TEAM_META, TEAM_IDS, buildDraftQueue, isExcludedFromTeams, isSittingOut, isHost,
  captainTeamOf, TEAM_GAMES_STORAGE_KEY,
} from '../data/teams';
import { prefersReducedMotion } from '../utils/motion';
import EmojiGuess from '../components/games/EmojiGuess';
import GuessIt from '../components/games/GuessIt';
import TeamDraft from '../components/games/TeamDraft';

// The reveal played before the draft: a 3-2-1-GO countdown, then the first team
// name held for a full 5 seconds, then the second held for another 5, then the
// draft starts on its own. [phase, delayMs] — the whole thing is on timers, so
// nobody has to touch the screen once it starts.
const NAME_HOLD_MS = 5000;
const HOST_HOLD_MS = 3000;
const COUNTDOWN_END_MS = 2550;
const NAMES_START_MS = COUNTDOWN_END_MS + HOST_HOLD_MS;
const REVEAL_SEQUENCE = [
  ['3', 0],
  ['2', 650],
  ['1', 1300],
  ['GO!', 1950],
  ['host', COUNTDOWN_END_MS],
  ['team:vikings', NAMES_START_MS],
  ['team:panthers', NAMES_START_MS + NAME_HOLD_MS],
];
// Both names shown and held — the overlay clears and the draft begins.
const REVEAL_TOTAL_MS = NAMES_START_MS + NAME_HOLD_MS * 2;

const TG_STORES = ['attendees'];
const LS_KEY = TEAM_GAMES_STORAGE_KEY;
const TEAM_ICONS = { vikings: Swords, panthers: Cat };

function loadPersisted() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function TeamGames() {
  const { attendees, loading } = useStores(TG_STORES);
  const [tab, setTab] = useState('teams');

  const [teams, setTeams] = useState(() => loadPersisted()?.teams || null);
  const [scores, setScores] = useState(() => loadPersisted()?.scores || { vikings: 0, panthers: 0 });

  // Bumped on every shuffle to (re)play the formation animation. Kept ephemeral
  // — a reload shows the saved teams instantly without re-dealing. A ref tracks
  // which key we've already animated so switching tabs back doesn't replay it.
  const [dealKey, setDealKey] = useState(0);
  const animatedKey = useRef(0);
  const animateFormation = dealKey > 0 && dealKey !== animatedKey.current && tab === 'teams';
  useEffect(() => { if (tab === 'teams') animatedKey.current = dealKey; }, [tab, dealKey]);

  // 3-2-1-GO countdown shown before the deal. `scorePop` drives the "+1" pop on
  // the scoreboard when a team is awarded a point. `phase` drives the pre-deal
  // reveal overlay (countdown → team-name unveils → VS).
  const [phase, setPhase] = useState(null);
  const [scorePop, setScorePop] = useState({ team: null, n: 0, points: 1 });
  const timers = useRef([]);
  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  useEffect(() => () => clearTimers(), []);

  // Persist teams + scores locally so an accidental refresh/navigation mid-game
  // doesn't wipe the board. (Client-only by design — see data/teams.js.)
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ teams, scores }));
    } catch { /* storage full or unavailable — non-fatal */ }
  }, [teams, scores]);

  // Anyone on the sit-out list is filtered out here, before the split and
  // before the "hosting" bucket — they do not appear in Team Games at all.
  const members = useMemo(
    () => attendees
      .filter((a) => (a.name || '').trim())
      .map((a) => ({ id: a.id, name: a.name, role: a.role, email: a.email }))
      .filter((m) => !isSittingOut(m)),
    [attendees]
  );

  // Captains + host sit out the draw; everyone else is an eligible player.
  const players = useMemo(() => members.filter((m) => !isExcludedFromTeams(m)), [members]);
  const sittingOut = useMemo(() => members.filter(isExcludedFromTeams), [members]);
  // The host fronts the whole evening; each captain fronts one team. They were
  // previously lumped into a single grey "not playing" row, which buried them.
  const host = useMemo(() => sittingOut.find(isHost) || null, [sittingOut]);
  const captains = useMemo(() => {
    const byTeam = {};
    for (const member of sittingOut) {
      const teamId = captainTeamOf(member);
      if (teamId && !byTeam[teamId]) byTeam[teamId] = member;
    }
    return byTeam;
  }, [sittingOut]);
  // Anyone sitting out who is neither the host nor a captain.
  const otherSitters = useMemo(
    () => sittingOut.filter((m) => !isHost(m) && !captainTeamOf(m)),
    [sittingOut]
  );

  // The draft in flight: the settled teams plus the (independently shuffled)
  // order they get revealed in. Held here so the ceremony only ever presents a
  // result that was already decided.
  const [draft, setDraft] = useState(null);

  const commitTeams = (nextTeams) => {
    setTeams(nextTeams);
    setScores({ vikings: 0, panthers: 0 });
    setDealKey((k) => k + 1);   // replay the deal-in animation
    toast.success('Teams are in — let the games begin!');
  };

  const revealTeams = () => {
    if (!players.length) {
      toast.error('No eligible players — add attendees (captains and the host sit out).');
      return;
    }
    clearTimers();
    setTab('teams');            // jump to the Teams view so everyone watches

    // Decide the split up front. The countdown, the swinging card and the
    // near-miss are all theatre over this already-settled result.
    const next = buildDraftQueue(players);

    // Reduced motion: skip the whole ceremony and show the teams.
    if (prefersReducedMotion()) { commitTeams(next.teams); return; }

    setDraft(next);
    REVEAL_SEQUENCE.forEach(([p, delay]) => {
      timers.current.push(setTimeout(() => setPhase(p), delay));
    });
    // When the name reveal finishes, hand over to the draft ceremony.
    timers.current.push(setTimeout(() => setPhase('draft'), REVEAL_TOTAL_MS));
  };

  const finishDraft = () => {
    if (draft) commitTeams(draft.teams);
    setDraft(null);
    setPhase(null);
  };

  const award = (teamId, points = 1) => {
    setScores((s) => ({ ...s, [teamId]: Math.max(0, (s[teamId] || 0) + points) }));
    setScorePop((p) => ({ team: teamId, n: p.n + 1, points }));
  };
  const resetScores = () => setScores({ vikings: 0, panthers: 0 });

  const hasTeams = Boolean(teams && (teams.vikings.length || teams.panthers.length));
  const leaderId = scores.vikings === scores.panthers ? null : (scores.vikings > scores.panthers ? 'vikings' : 'panthers');

  if (loading) return <PageLoader label="Loading team games…" sublabel="Pulling in the roster" />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold font-display text-slate-900 flex items-center gap-2">
            <Gamepad2 style={{ color: 'var(--accent)' }} size={24} />
            <span>Team Games</span>
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Split the crew into two teams and run engaging games on the big screen. {players.length} player{players.length === 1 ? '' : 's'}
            {host ? ` · hosted by ${host.name}` : ''}.
          </p>
        </div>
        <button
          onClick={revealTeams}
          className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-semibold transition flex items-center gap-2 self-start sm:self-auto shadow-sm"
        >
          <Shuffle size={16} />
          <span>{hasTeams ? 'Redraw Teams' : 'Reveal Teams'}</span>
        </button>
      </div>

      {/* Scoreboard */}
      {hasTeams && (
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          {TEAM_IDS.map((id) => {
            const meta = TEAM_META[id];
            const Icon = TEAM_ICONS[id];
            const leading = leaderId === id;
            return (
              <div
                key={id}
                className="rounded-xl p-4 border-2 flex items-center justify-between gap-3 transition"
                style={{ borderColor: leading ? meta.color : 'transparent', background: meta.soft }}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Icon size={16} style={{ color: meta.color }} className="shrink-0" />
                    <span className="font-bold text-slate-900 truncate">{meta.name}</span>
                    {leading && <Crown size={14} className="text-amber-500 shrink-0" />}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{teams[id].length} player{teams[id].length === 1 ? '' : 's'}</div>
                </div>
                <div className="relative text-3xl sm:text-4xl font-extrabold font-display shrink-0" style={{ color: meta.color }}>
                  {/* Prefixed keys so the score span and the floating "+N" span
                      never share a key (they're siblings and their raw numbers
                      can coincide), which previously broke reconciliation and
                      made the digits pile up instead of replacing. */}
                  <span key={`score-${scores[id] || 0}`} className="tg-score-pop">{scores[id] || 0}</span>
                  {scorePop.team === id && (
                    <span key={`pop-${scorePop.n}`} className="tg-float absolute -top-3 -right-1 text-base" aria-hidden="true">+{scorePop.points}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white border border-slate-200 rounded-xl p-1.5 shadow-sm flex gap-1 overflow-x-auto">
        {[
          { id: 'teams', label: 'Teams', icon: Users },
          { id: 'emoji', label: 'Emoji Guess', icon: Smile },
          { id: 'guess', label: 'Guess It!', icon: Lightbulb },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition ${tab === t.id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {tab === 'teams' ? (
        <TeamsView
          key={dealKey}
          members={players}
          host={host}
          captains={captains}
          otherSitters={otherSitters}
          teams={teams}
          hasTeams={hasTeams}
          onShuffle={revealTeams}
          animate={animateFormation}
        />
      ) : !hasTeams ? (
        <EmptyPrompt onShuffle={revealTeams} hasMembers={members.length > 0} />
      ) : tab === 'emoji' ? (
        <EmojiGuess scores={scores} onAward={award} onResetScores={resetScores} />
      ) : (
        <GuessIt scores={scores} onAward={award} onResetScores={resetScores} />
      )}

      {/* Pre-draft reveal overlay: countdown → team-name unveils → VS */}
      {phase !== null && phase !== 'draft' && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-6"
          style={{ background: 'rgba(15,23,42,0.82)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
        >
          <RevealOverlay phase={phase} host={host} captains={captains} />
        </div>
      )}

      {/* The draft ceremony — one name at a time, locked in by the host */}
      {phase === 'draft' && draft && (
        <TeamDraft queue={draft.queue} captains={captains} onDone={finishDraft} onSkip={finishDraft} />
      )}
    </div>
  );
}

// The animated content inside the pre-deal overlay. Keyed by phase so each step
// re-triggers its entrance animation.
function RevealOverlay({ phase, host, captains = {} }) {
  if (['3', '2', '1', 'GO!'].includes(phase)) {
    return (
      <div
        key={phase}
        className="tg-count font-display font-extrabold leading-none"
        style={{ color: phase === 'GO!' ? 'var(--accent)' : '#fff', fontSize: 'clamp(5rem, 28vw, 12rem)' }}
      >
        {phase}
      </div>
    );
  }

  if (phase === 'host') {
    return (
      <div key={phase} className="tg-winner text-center">
        <div className="text-sm sm:text-base font-bold uppercase tracking-[0.4em] text-white/70 mb-6">
          Your host tonight
        </div>
        <div
          className="mx-auto mb-6 rounded-full flex items-center justify-center tg-host-pulse"
          style={{ width: 108, height: 108, background: 'rgba(249,101,22,0.18)', border: '2px solid var(--accent)' }}
        >
          <Mic2 size={52} style={{ color: '#fff' }} />
        </div>
        <div
          className="font-display font-extrabold leading-none"
          style={{ color: '#fff', fontSize: 'clamp(2.75rem, 12vw, 6rem)', textShadow: '0 0 40px var(--accent)' }}
        >
          {host ? host.name : 'Srinivas'}
        </div>
      </div>
    );
  }

  if (phase === 'team:vikings' || phase === 'team:panthers') {
    const id = phase.split(':')[1];
    const meta = TEAM_META[id];
    const Icon = TEAM_ICONS[id];
    const label = id === 'vikings' ? 'Team One' : 'Team Two';
    return (
      <div key={phase} className="tg-winner text-center">
        <div className="text-sm sm:text-base font-bold uppercase tracking-[0.4em] text-white/70 mb-6">{label}</div>
        <div
          className="mx-auto mb-6 rounded-3xl flex items-center justify-center tg-trophy"
          style={{ width: 108, height: 108, background: `${meta.color}26`, border: `2px solid ${meta.color}` }}
        >
          <Icon size={56} style={{ color: '#fff' }} />
        </div>
        <div
          className="font-display font-extrabold leading-none"
          style={{ color: '#fff', fontSize: 'clamp(2.75rem, 12vw, 6rem)', textShadow: `0 0 40px ${meta.color}` }}
        >
          {meta.name}
        </div>
        {captains[id] && (
          <div className="mt-6 inline-flex items-center gap-2 text-sm sm:text-base font-semibold text-white/75">
            <Crown size={16} className="text-amber-300" />
            <span>Captain {captains[id].name}</span>
          </div>
        )}
      </div>
    );
  }

  if (phase === 'vs') {
    return (
      <div key={phase} className="tg-winner w-full max-w-3xl text-center space-y-4">
        {TEAM_IDS.map((id, i) => (
          <React.Fragment key={id}>
            <div
              className="font-display font-extrabold leading-none"
              style={{ color: '#fff', fontSize: 'clamp(2rem, 9vw, 4.5rem)', textShadow: `0 0 30px ${TEAM_META[id].color}` }}
            >
              {TEAM_META[id].name}
            </div>
            {i === 0 && (
              <div className="font-display font-black text-white/90" style={{ fontSize: 'clamp(1.5rem, 6vw, 3rem)', letterSpacing: '0.1em' }}>VS</div>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  }

  return null;
}

// Milliseconds between each player landing on a team during the reveal.
const DEAL_STEP_MS = 90;

function TeamsView({ members, host, captains = {}, otherSitters = [], teams, hasTeams, onShuffle, animate }) {
  // Capture the "should I animate?" intent once at mount so a stray re-render
  // can't cut the deal short. The parent remounts this via key={dealKey}, so a
  // new shuffle gives a fresh mount (and a fresh `play`).
  const [play] = useState(Boolean(animate));

  // Interleave the two rosters so players deal in alternately (Viking, Panther,
  // Viking, …) rather than one whole team then the other. Guarded for the
  // no-teams case so the hooks above always run first.
  const dealOrder = {};
  let seq = 0;
  if (teams) {
    const maxLen = Math.max(teams.vikings.length, teams.panthers.length);
    for (let i = 0; i < maxLen; i++) {
      if (teams.vikings[i]) dealOrder[teams.vikings[i].id] = seq++;
      if (teams.panthers[i]) dealOrder[teams.panthers[i].id] = seq++;
    }
  }
  const totalMs = play ? seq * DEAL_STEP_MS + 420 : 0;

  // Hide the "Forming teams…" banner once the last player has landed.
  const [dealing, setDealing] = useState(play);
  useEffect(() => {
    if (!play) return undefined;
    const t = setTimeout(() => setDealing(false), totalMs);
    return () => clearTimeout(t);
  }, [play, totalMs]);

  if (!members.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-16 text-center shadow-sm">
        <Users className="mx-auto text-slate-300 mb-3" size={36} />
        <h3 className="text-base font-bold text-slate-800 font-display">No members yet</h3>
        <p className="text-sm text-slate-500 mt-1">Add people on the Attendees page, then come back to build teams.</p>
      </div>
    );
  }

  if (!hasTeams) {
    return <EmptyPrompt onShuffle={onShuffle} hasMembers />;
  }

  return (
    <div className="space-y-3">
      {dealing && (
        <div className="flex items-center justify-center gap-2 text-sm font-semibold text-slate-500 tg-panel-in">
          <Shuffle size={15} className="animate-spin" style={{ color: 'var(--accent)', animationDuration: '1.2s' }} />
          <span>Forming teams…</span>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {TEAM_IDS.map((id) => {
          const meta = TEAM_META[id];
          const Icon = TEAM_ICONS[id];
          return (
            <div
              key={id}
              className={`bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden ${play ? 'tg-panel-in' : ''}`}
            >
              <div className="px-4 py-3" style={{ background: meta.soft }}>
                <div className="flex items-center gap-2">
                  <Icon size={18} style={{ color: meta.color }} />
                  <span className="font-bold text-slate-900">{meta.name}</span>
                  <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full text-white" style={{ background: meta.color }}>
                    {teams[id].length}
                  </span>
                </div>
                {captains[id] && (
                  <div className="mt-1 flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                    <Crown size={12} className="text-amber-500 shrink-0" />
                    <span className="truncate">Captain {captains[id].name}</span>
                  </div>
                )}
              </div>
              <ul className="divide-y divide-slate-100">
                {teams[id].map((m) => (
                  <li
                    key={m.id}
                    className={`px-4 py-2.5 flex items-center gap-3 ${play ? 'tg-deal' : ''}`}
                    style={play ? { animationDelay: `${(dealOrder[m.id] || 0) * DEAL_STEP_MS}ms` } : undefined}
                  >
                    <span
                      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                      style={{ background: meta.color }}
                    >
                      {(m.name || '?').trim().charAt(0).toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-800 text-sm truncate">{m.name}</div>
                      {m.role && <div className="text-xs text-slate-400 truncate">{m.role}</div>}
                    </div>
                  </li>
                ))}
                {teams[id].length === 0 && (
                  <li className="px-4 py-6 text-center text-xs text-slate-400 italic">No players on this team</li>
                )}
              </ul>
            </div>
          );
        })}
      </div>

      {host && (
        <div
          className="flex items-center gap-3 rounded-xl px-4 py-3 border"
          style={{ background: 'rgba(249,101,22,0.07)', borderColor: 'rgba(249,101,22,0.35)' }}
        >
          <span
            className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
            style={{ background: 'var(--accent)' }}
          >
            <Mic2 size={18} className="text-white" />
          </span>
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Your host</div>
            <div className="font-bold text-slate-900 truncate">{host.name}</div>
          </div>
        </div>
      )}

      {otherSitters.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
          <Users size={14} className="text-slate-400 shrink-0" />
          <span className="font-semibold">Also sitting out:</span>
          {otherSitters.map((m) => (
            <span key={m.id} className="px-2 py-0.5 rounded-full bg-white border border-slate-200 font-medium text-slate-600">{m.name}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyPrompt({ onShuffle, hasMembers }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
      <Trophy className="mx-auto text-slate-300 mb-3" size={36} />
      <h3 className="text-base font-bold text-slate-800 font-display">Form the teams to begin</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
        {hasMembers
          ? 'Tap below to run the draft — every name swings between the two teams until you lock it in.'
          : 'Add people on the Attendees page first — teams are built from the attendee list.'}
      </p>
      {hasMembers && (
        <button
          onClick={onShuffle}
          className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-semibold transition"
        >
          <Shuffle size={16} /> Reveal Teams
        </button>
      )}
      <div className="mt-6 inline-flex items-start gap-2 text-xs text-slate-400 max-w-sm mx-auto text-left">
        <Info size={14} className="shrink-0 mt-0.5" />
        <span>Tip: put this on the big screen. Teams shout their guesses and you award points — no logins or phones needed.</span>
      </div>
    </div>
  );
}
